import { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

/**
 * MentionTextarea — drop-in replacement for <Textarea /> that supports
 * @user typeahead. When a user is selected from the suggestion popover, their
 * @handle is inserted in-place AND their user_id is appended to onMentionsChange.
 *
 * Props:
 *   - value, onChange, placeholder, maxLength, rows, className, disabled
 *   - onMentionsChange: (user_ids: string[]) => void  (called when selections
 *     mutate so the parent can submit them with the comment)
 *   - testIdPrefix: prefix for data-testids (e.g. "drawer-comment")
 */
const MentionTextarea = ({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  maxLength,
  rows = 3,
  className = "",
  disabled = false,
  testIdPrefix = "mention",
}) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Track resolved {user_id, name} pairs so the parent can submit them.
  const [mentions, setMentions] = useState([]);

  // Keep parent in sync — only emit IDs that are still referenced in the text
  useEffect(() => {
    if (!onMentionsChange) return;
    const text = value || "";
    const live = mentions.filter((m) => text.includes(`@${m.handle}`));
    onMentionsChange(live.map((m) => m.user_id));
    if (live.length !== mentions.length) {
      setMentions(live);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const detectMention = (text, caret) => {
    // walk backwards from caret to find a "@..." token without whitespace
    let i = caret - 1;
    while (i >= 0 && !/\s/.test(text[i])) i -= 1;
    const tokenStart = i + 1;
    const token = text.slice(tokenStart, caret);
    if (token.startsWith("@") && token.length >= 1) {
      return { tokenStart, q: token.slice(1) };
    }
    return null;
  };

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 1) {
      setResults([]);
      return;
    }
    try {
      const r = await api.get(`/users/search-mentions`, { params: { q, limit: 6 } });
      setResults(r.data?.users || []);
      setActiveIndex(0);
    } catch (_e) {
      setResults([]);
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange?.(text);
    const caret = e.target.selectionStart || text.length;
    const m = detectMention(text, caret);
    if (m) {
      setOpen(true);
      setQuery(m.q);
      fetchSuggestions(m.q);
    } else {
      setOpen(false);
      setQuery("");
    }
  };

  const insertMention = (user) => {
    const handle = (user.artist_name || user.name || "").trim().replace(/\s+/g, "_");
    if (!handle) return;
    const el = ref.current;
    const text = value || "";
    const caret = el?.selectionStart || text.length;
    const m = detectMention(text, caret);
    if (!m) return;
    const before = text.slice(0, m.tokenStart);
    const after = text.slice(caret);
    const inserted = `@${handle} `;
    const newText = before + inserted + after;
    onChange?.(newText);
    // Track this mention
    const next = [...mentions.filter((x) => x.handle !== handle), { user_id: user.user_id, handle, name: handle }];
    setMentions(next);
    onMentionsChange?.(next.map((x) => x.user_id));
    setOpen(false);
    setQuery("");
    // Restore caret after the inserted handle + space
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = before.length + inserted.length;
      el.selectionStart = pos;
      el.selectionEnd = pos;
      el.focus();
    });
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      insertMention(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        disabled={disabled}
        className={className}
        data-testid={`${testIdPrefix}-input`}
      />
      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 bottom-full mb-1 max-h-56 overflow-y-auto z-30 rounded-lg border border-purple-500/30 bg-[#11091e] shadow-xl"
          data-testid={`${testIdPrefix}-mention-popover`}
        >
          {results.map((u, idx) => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => insertMention(u)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                idx === activeIndex
                  ? "bg-purple-500/30"
                  : "hover:bg-purple-500/15"
              }`}
              data-testid={`${testIdPrefix}-mention-option-${u.user_id}`}
            >
              <img
                src={getProfilePhotoUrl(u)}
                alt=""
                className="w-7 h-7 rounded-full object-cover"
              />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">@{(u.artist_name || u.name || "user").replace(/\s+/g, "_")}</p>
                {u.artist_name && u.name && u.artist_name !== u.name && (
                  <p className="text-[10px] text-gray-500 truncate">{u.name}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
